/**
 * Cielo y luz de La Oficina.
 *
 * El cielo sigue la hora local: amanecer, día, atardecer y noche, con sol,
 * luna, estrellas, nubes y una ciudad al fondo. La luz interior se pinta en
 * una capa de baja resolución (un pixel lógico) y se monta sobre cada piso:
 * primero la penumbra, con huecos donde hay lámparas, y luego los brillos.
 */
import { PX, hashId, mix, rgba } from './pixelKit';

const SKY_KEYS = [
    [0, '#0b1030', '#1c2452'],
    [5, '#141a44', '#3a3468'],
    [6.5, '#5a5aa8', '#f2a878'],
    [8, '#6fb4ea', '#cfe8f8'],
    [12, '#58a8ea', '#bfe2fa'],
    [16.5, '#6aaee8', '#dbe9f0'],
    [18.5, '#7a5aa8', '#f2a070'],
    [19.6, '#2a2458', '#6a4a78'],
    [21, '#0b1030', '#1c2452'],
    [24, '#0b1030', '#1c2452'],
];

function ramp(value, from, to) {
    return Math.max(0, Math.min(1, (value - from) / (to - from)));
}

/** Colores del cielo y nivel de oscuridad (0 día, 1 noche) para una hora dada. */
export function skyAt(hour, storm) {
    let index = 0;
    while (index < SKY_KEYS.length - 2 && hour >= SKY_KEYS[index + 1][0]) index += 1;
    const a = SKY_KEYS[index];
    const b = SKY_KEYS[index + 1];
    const t = ramp(hour, a[0], b[0]);
    let top = mix(a[1], b[1], t);
    let low = mix(a[2], b[2], t);
    let dark = hour < 12 ? 1 - ramp(hour, 5, 7.5) : ramp(hour, 17.5, 19.75);
    if (storm) {
        top = mix(top, '#3a4252', 0.7);
        low = mix(low, '#5a6272', 0.7);
        dark = Math.max(dark, 0.5);
    }
    return { top, low, dark };
}

function puff(ctx, x, y, scale, color) {
    ctx.fillStyle = color;
    const u = PX * scale;
    ctx.fillRect(Math.round(x), Math.round(y), 14 * u, 3 * u);
    ctx.fillRect(Math.round(x + 2 * u), Math.round(y - 2 * u), 9 * u, 2 * u);
    ctx.fillRect(Math.round(x + 4 * u), Math.round(y - 4 * u), 5 * u, 2 * u);
}

function orb(ctx, x, y, color, glowColor) {
    const cx = Math.round(x);
    const cy = Math.round(y);
    ctx.fillStyle = rgba(glowColor, 0.18);
    ctx.fillRect(cx - 30, cy - 30, 60, 60);
    ctx.fillStyle = rgba(glowColor, 0.22);
    ctx.fillRect(cx - 24, cy - 24, 48, 48);
    ctx.fillStyle = color;
    ctx.fillRect(cx - 12, cy - 18, 24, 36);
    ctx.fillRect(cx - 18, cy - 12, 36, 24);
    ctx.fillRect(cx - 15, cy - 15, 30, 30);
}

/**
 * Cielo en coordenadas de pantalla. `drift` desplaza la ciudad del fondo para
 * dar profundidad cuando se mueve la cámara.
 */
export function drawSky(ctx, w, h, env, drift) {
    const gradient = ctx.createLinearGradient(0, 0, 0, h);
    gradient.addColorStop(0, env.flash ? '#e8eeff' : env.sky.top);
    gradient.addColorStop(1, env.flash ? '#ffffff' : env.sky.low);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, w, h);

    if (env.dark > 0.45 && !env.flash) {
        for (let i = 0; i < 70; i += 1) {
            const seed = hashId(`estrella-${i}`);
            if (Math.floor((env.now + seed) / (500 + (seed % 700))) % 5 === 0) continue;
            ctx.fillStyle = rgba('#fff6c8', (env.dark - 0.45) * 1.6);
            const size = seed % 7 === 0 ? 3 : 2;
            ctx.fillRect(seed % Math.max(1, Math.floor(w)), (seed >>> 9) % Math.max(1, Math.floor(h * 0.7)), size, size);
        }
    }

    const hour = env.hour;
    if (!env.storm) {
        if (hour > 5.8 && hour < 19.4) {
            const t = (hour - 5.8) / 13.6;
            orb(ctx, w * (0.08 + t * 0.84), h * 0.62 - Math.sin(t * Math.PI) * h * 0.5, '#ffe680', '#fff0a0');
        } else {
            const t = ((hour + 24 - 19.4) % 24) / 10.4;
            const mx = w * (0.1 + t * 0.8);
            const my = h * 0.5 - Math.sin(t * Math.PI) * h * 0.38;
            orb(ctx, mx, my, '#eef2ff', '#b8c8ff');
            ctx.fillStyle = '#c8d0ee';
            ctx.fillRect(Math.round(mx) - 9, Math.round(my) - 6, 6, 6);
            ctx.fillRect(Math.round(mx) + 3, Math.round(my) + 3, 9, 6);
        }
    }

    const cloud = env.storm ? '#4a5262' : mix('#ffffff', '#3a3f60', env.dark * 0.85);
    for (let i = 0; i < 6; i += 1) {
        const seed = hashId(`nube-${i}`);
        const span = w + 400;
        const x = ((env.now / (38 + (seed % 30)) + seed) % span) - 200;
        puff(ctx, x, 30 + ((seed >>> 5) % Math.max(1, Math.floor(h * 0.42))), 1 + (seed % 2), cloud);
    }

    const city = mix('#8a9ab8', '#141830', Math.max(env.dark, env.storm ? 0.6 : 0));
    let x = -((drift % 60) + 60);
    let index = Math.floor(drift / 60);
    while (x < w) {
        const seed = hashId(`torre-${index}`);
        const tall = 60 + (seed % 140);
        const wide = 60;
        ctx.fillStyle = seed % 2 ? city : mix(city, '#000000', 0.12);
        ctx.fillRect(Math.round(x), h - tall, wide - 4, tall);
        if (env.dark > 0.4) {
            for (let row = 0; row < Math.floor(tall / 18); row += 1) {
                for (let col = 0; col < 4; col += 1) {
                    if ((seed >>> (row + col * 3)) % 3 !== 0) continue;
                    ctx.fillStyle = rgba('#ffe7a0', 0.75 * env.dark);
                    ctx.fillRect(Math.round(x) + 8 + col * 12, h - tall + 10 + row * 18, 6, 8);
                }
            }
        }
        x += wide;
        index += 1;
    }

    if (env.event && env.event.id === 'plane') {
        const t = env.event.age / env.event.ms;
        const px = -260 + t * (w + 520);
        const py = h * 0.2 + Math.sin(t * 9) * 8;
        ctx.fillStyle = '#f7f3ea';
        ctx.fillRect(px - 250, py - 12, 200, 30);
        ctx.fillStyle = '#c0392b';
        ctx.fillRect(px - 250, py - 12, 200, 3);
        ctx.fillRect(px - 250, py + 15, 200, 3);
        ctx.font = 'bold 15px monospace';
        ctx.fillText('CENTRAL MEDIA', px - 230, py + 10);
        ctx.fillStyle = '#5a6270';
        ctx.fillRect(px - 50, py + 2, 50, 2);
        ctx.fillStyle = '#e0503c';
        ctx.fillRect(px, py - 6, 54, 15);
        ctx.fillRect(px + 12, py - 15, 24, 9);
        ctx.fillRect(px - 6, py - 18, 12, 15);
        ctx.fillStyle = '#8fd0ea';
        ctx.fillRect(px + 36, py - 3, 12, 6);
        ctx.fillStyle = '#2a3038';
        ctx.fillRect(px + 54, py - (Math.floor(env.now / 50) % 2 ? 12 : 0), 3, 15);
    }

    if (env.storm) {
        ctx.fillStyle = rgba('#d6ecff', 0.55);
        for (let i = 0; i < 90; i += 1) {
            const seed = hashId(`gota-${i}`);
            const fall = (env.now * (0.5 + (seed % 5) / 10) + seed) % (h + 40);
            ctx.fillRect(((seed >>> 4) % Math.max(1, Math.floor(w))) - fall * 0.12, fall - 20, 2, 12);
        }
        if (env.flash) {
            ctx.fillStyle = '#ffffff';
            let bx = w * (0.2 + ((env.event.seed % 60) / 100));
            let by = 0;
            for (let i = 0; i < 9; i += 1) {
                const nx = bx + ((hashId(`rayo-${i}-${Math.floor(env.event.age / 3000)}`) % 60) - 30);
                ctx.fillRect(Math.min(bx, nx), by, Math.abs(nx - bx) + 6, 6);
                ctx.fillRect(nx, by, 6, 34);
                bx = nx;
                by += 34;
            }
        }
    }
}

/** Capa reutilizable para la luz de un piso. */
export function createLightLayer() {
    return { dark: document.createElement('canvas'), glow: document.createElement('canvas') };
}

/**
 * Monta penumbra y brillos sobre el rectángulo (x, y, w, h) del mundo.
 * `zones` son tramos { x, w, alpha } de penumbra; `lights` son focos
 * { x, y, r, color, a } en coordenadas de mundo.
 */
export function lightFloor(ctx, layer, x, y, w, h, zones, lights, tint) {
    const cw = Math.ceil(w / PX);
    const ch = Math.ceil(h / PX);
    [layer.dark, layer.glow].forEach((canvas) => {
        if (canvas.width !== cw || canvas.height !== ch) {
            canvas.width = cw;
            canvas.height = ch;
        }
    });
    const dark = layer.dark.getContext('2d');
    const glow = layer.glow.getContext('2d');
    dark.globalCompositeOperation = 'source-over';
    dark.clearRect(0, 0, cw, ch);
    zones.forEach((zone) => {
        dark.fillStyle = rgba(tint || '#0c0e2c', zone.alpha);
        dark.fillRect(Math.floor((zone.x - x) / PX), 0, Math.ceil(zone.w / PX), ch);
    });
    glow.globalCompositeOperation = 'source-over';
    glow.clearRect(0, 0, cw, ch);
    glow.globalCompositeOperation = 'lighter';
    dark.globalCompositeOperation = 'destination-out';
    lights.forEach((light) => {
        const lx = (light.x - x) / PX;
        const ly = (light.y - y) / PX;
        const radius = Math.max(2, light.r / PX);
        if (lx < -radius || lx > cw + radius) return;
        const hole = dark.createRadialGradient(lx, ly, 0, lx, ly, radius);
        hole.addColorStop(0, `rgba(0,0,0,${Math.min(1, light.a * 1.5)})`);
        hole.addColorStop(1, 'rgba(0,0,0,0)');
        dark.fillStyle = hole;
        if (light.shaft) {
            dark.beginPath();
            dark.moveTo(lx - 10, ly - 12);
            dark.lineTo(lx + 10, ly - 12);
            dark.lineTo(lx + 10 + light.lean, ch);
            dark.lineTo(lx - 10 + light.lean, ch);
            dark.fill();
            glow.fillStyle = rgba(light.color, light.a * 0.26);
            glow.beginPath();
            glow.moveTo(lx - 10, ly - 12);
            glow.lineTo(lx + 10, ly - 12);
            glow.lineTo(lx + 10 + light.lean, ch);
            glow.lineTo(lx - 10 + light.lean, ch);
            glow.fill();
            return;
        }
        dark.fillRect(lx - radius, ly - radius, radius * 2, radius * 2);
        const halo = glow.createRadialGradient(lx, ly, 0, lx, ly, radius);
        halo.addColorStop(0, rgba(light.color, Math.min(1, light.a * 0.62)));
        halo.addColorStop(1, rgba(light.color, 0));
        glow.fillStyle = halo;
        if (light.cone) {
            glow.beginPath();
            glow.moveTo(lx - 3, ly);
            glow.lineTo(lx + 3, ly);
            glow.lineTo(lx + radius * 0.75, ly + radius);
            glow.lineTo(lx - radius * 0.75, ly + radius);
            glow.fill();
        } else {
            glow.fillRect(lx - radius, ly - radius, radius * 2, radius * 2);
        }
    });
    ctx.drawImage(layer.dark, x, y, cw * PX, ch * PX);
    ctx.globalCompositeOperation = 'lighter';
    ctx.drawImage(layer.glow, x, y, cw * PX, ch * PX);
    ctx.globalCompositeOperation = 'source-over';
}
